/**
 * Prospect view until a policy is active, then client view. Only the Plan
 * tab's label and content change; the tabs themselves never move. The last
 * known mode is remembered so a cold start does not flicker.
 */
import { useEffect } from "react";
import { useJourneyFacts } from "@/hooks/useJourneyFacts";
import type { JourneyFacts } from "@/data/types";

export type ViewMode = "prospect" | "client";

const STORAGE_KEY = "tfwp.viewMode";
// issued_paid: the first premium is paid, so the policy is theirs. Client view.
const CLIENT_STAGES = new Set(["issued_paid", "active", "renewing"]);

function readRemembered(): ViewMode {
  try {
    return localStorage.getItem(STORAGE_KEY) === "client" ? "client" : "prospect";
  } catch {
    return "prospect";
  }
}

export function decideViewMode(facts: Pick<JourneyFacts, "policy" | "contactStage">): ViewMode {
  if (facts.policy?.lifecycle === "active") return "client";
  if (facts.contactStage && CLIENT_STAGES.has(facts.contactStage)) return "client";
  return "prospect";
}

export function useViewMode(): { mode: ViewMode; resolved: boolean } {
  const facts = useJourneyFacts();
  const mode = facts.data ? decideViewMode(facts.data) : undefined;

  useEffect(() => {
    if (!mode) return;
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      /* private mode */
    }
  }, [mode]);

  return { mode: mode ?? readRemembered(), resolved: facts.isSuccess };
}
